/**
 * OverlayPicker — Face overlay picker UI.
 *
 * 2026-09-18 (PROMPT F 20 Phase 1) — Implements F 19 Phase 6a design.
 *
 * Shows the catalog, lets the user:
 *   - Preview an overlay (iframe if available, static thumbnail fallback)
 *   - Pick a scope (Account / Project / Surface, default Account)
 *   - See the diff BEFORE applying (per operator rule)
 *   - Apply / Revert / Cancel
 *
 * Wired into Settings.tsx as a new "Face" tab and exposed as the
 * `OverlayPickerModal` for any other consumer (e.g. command palette —
 * not yet implemented; palette doesn't exist in the codebase).
 *
 * Operator-approved design (F 19 Phase 6a, all 5 Y/Y):
 *   1. Settings + palette only (sidebar chip deferred)
 *   2. iframe + static thumbnail fallback
 *   3. Default scope: Account
 *   4. Explicit Revert button always visible
 *   5. Empty state links to catalog
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Stack,
  Group,
  Text,
  Button,
  Card,
  Badge,
  Modal,
  Radio,
  Alert,
  Loader,
  ScrollArea,
  Divider,
} from '@mantine/core';
import {
  IconEye,
  IconCheck,
  IconTrash,
  IconX,
  IconInfoCircle,
} from '@tabler/icons-react';

import type { AppOverlay } from '../../../types/models/AppOverlay';
import {
  type FacePreferenceStorage,
  type FaceScope,
} from '../../../core/overlays/facePreference';
import {
  applyOverlay,
  revertOverlay,
  findCurrentlyApplied,
  diffApproved,
} from '../../../core/overlays/apply';
import {
  CATALOG,
  filterCatalog,
  registerCatalogOnLoad,
  type CatalogEntry,
} from '../../../core/overlays/catalog';
import {
  setPendingDiff,
  clearPendingDiff,
  setPreview,
  clearPreview,
  setError,
  clearError,
  getUIState,
} from '../../../core/overlays/uiStore';
import { resolveOverlay, DEFAULT_OVERLAY_ID } from '../../../core/overlays/resolver';
import { getOverlayRegistry } from '../../../core/overlays/registry';
import { diffTopFields } from '../../../core/overlays/overlayBundle';
import type { Theme } from '../../../integration/embedding/ThemeAPI';
import { applyTheme } from '../../../integration/embedding/ThemeEngine';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface OverlayPickerProps {
  /** Required — storage adapter for FacePreference. */
  storage: FacePreferenceStorage;
  /** Current scope (defaults to Account-level for the current user). */
  scope?: FaceScope;
  /** Optional theme applier — defaults to ThemeEngine.applyTheme. */
  applyThemeFn?: (theme: Theme) => void;
  /** Called when the picker closes (after apply/revert/cancel). */
  onClose?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OverlayPicker({
  storage,
  scope: initialScope,
  applyThemeFn = applyTheme,
  onClose,
}: OverlayPickerProps) {
  // Ensure catalog is registered on first mount.
  useEffect(() => {
    registerCatalogOnLoad();
  }, []);

  const [scope, setScope] = useState<FaceScope>(
    initialScope ?? { userId: initialScope?.userId ?? 'current' },
  );
  const [candidateId, setCandidateId] = useState<string | null>(null);
  const [previewKind, setPreviewKind] = useState<'iframe' | 'thumbnail'>('thumbnail');
  const [applying, setApplying] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [diffData, setDiffData] = useState<{
    current: AppOverlay | null;
    candidate: AppOverlay;
    changedFields: string[];
  } | null>(null);
  const [applied, setApplied] = useState<AppOverlay | null>(null);
  const [error, setLocalError] = useState<string | null>(null);

  const readyEntries = useMemo(() => filterCatalog({ status: 'ready' }), []);

  // Resolve what's currently applied (for diff display).
  const refreshApplied = useCallback(async () => {
    const result = await resolveOverlay(storage, (id) => {
      const entry = CATALOG.find((e) => e.id === id);
      return entry ? entry.overlay : getOverlayRegistry().get(id);
    }, scope);
    if (result.ok) {
      setApplied(result.resolved.overlay);
    }
  }, [storage, scope]);

  useEffect(() => {
    refreshApplied();
  }, [refreshApplied]);

  // Start preview when a candidate is selected.
  useEffect(() => {
    if (!candidateId) {
      clearPreview();
      return;
    }
    const entry = CATALOG.find((e) => e.id === candidateId);
    if (!entry) return;
    setPreview({
      overlayId: entry.overlay.id,
      scope,
      kind: previewKind,
      startedAt: Date.now(),
    });
    return () => clearPreview();
  }, [candidateId, scope, previewKind]);

  // ---- Handlers ----

  const handleSelectCandidate = useCallback((entry: CatalogEntry) => {
    setCandidateId(entry.overlay.id);
    // 2026-09-18 (PROMPT F 21) — previewKind was inverted in F 20:
    // should be 'iframe' when previewIframe URL is set, NOT when
    // previewThumbnail is set. The overlay type now carries
    // previewIframe?: string (added in this dispatch).
    setPreviewKind(entry.overlay.previewIframe ? 'iframe' : 'thumbnail');
    setError(null);
  }, []);

  const handleRequestApply = useCallback(async () => {
    if (!candidateId) return;
    const entry = CATALOG.find((e) => e.id === candidateId);
    if (!entry) return;

    // Compute diff
    const current = applied;
    const changedFields = current
      ? diffTopFields(current, entry.overlay)
      : Object.keys(entry.overlay);
    setDiffData({ current, candidate: entry.overlay, changedFields });
    setShowDiff(true);
  }, [candidateId, applied]);

  const handleConfirmApply = useCallback(async () => {
    if (!diffData) return;
    setApplying(true);
    setLocalError(null);
    clearError();
    setPendingDiff({
      candidate: diffData.candidate,
      current: diffData.current,
      changedFields: diffData.changedFields,
    });
    try {
      const r = await applyOverlay(storage, diffData.candidate, scope, {
        diffConfirmed: true,
        applyTheme: applyThemeFn,
      });
      if (r.ok) {
        setApplied(r.applied);
        setShowDiff(false);
        setCandidateId(null);
        clearPendingDiff();
        await refreshApplied();
      } else {
        setLocalError(`${r.error}: ${r.detail ?? ''}`);
        setError({
          overlayId: diffData.candidate.id,
          scope,
          reason: r.error,
          timestamp: Date.now(),
        });
      }
    } catch (e) {
      setLocalError(String(e));
    } finally {
      setApplying(false);
    }
  }, [diffData, storage, scope, applyThemeFn, refreshApplied]);

  const handleRevert = useCallback(async () => {
    setApplying(true);
    setLocalError(null);
    try {
      const r = await revertOverlay(storage, scope, { applyTheme: applyThemeFn });
      if (r.ok) {
        setApplied(r.applied);
        await refreshApplied();
      } else {
        setLocalError(`${r.error}: ${r.detail ?? ''}`);
      }
    } catch (e) {
      setLocalError(String(e));
    } finally {
      setApplying(false);
    }
  }, [storage, scope, applyThemeFn, refreshApplied]);

  const handleCancel = useCallback(() => {
    setCandidateId(null);
    setShowDiff(false);
    setDiffData(null);
    onClose?.();
  }, [onClose]);

  // 2026-09-18 (PROMPT F 21) — Diff-modal cancel clears the candidate.
  // F 20 bug: cancelling the diff modal only closed the modal but left
  // candidateId set, so the catalog card stayed highlighted and the
  // preview area stayed rendered. Now cancelling the diff fully resets.
  const handleDiffCancel = useCallback(() => {
    setShowDiff(false);
    setDiffData(null);
    setCandidateId(null);
    clearPendingDiff();
    setError(null);
  }, []);

  // ---- Render ----

  if (readyEntries.length === 0) {
    return (
      <Stack gap="md" p="md">
        <Alert icon={<IconInfoCircle />} color="blue" title="No overlays installed">
          No overlays in the catalog yet.{' '}
          <a href="#catalog">Browse the catalog →</a>
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="md" p="md">
      {error && (
        <Alert icon={<IconInfoCircle />} color="red" title="Error">
          {error}
        </Alert>
      )}

      {/* Currently applied */}
      <Box>
        <Text size="sm" c="dimmed">Currently applied</Text>
        <Text fw={500}>
          {applied
            ? `${applied.name} v${applied.version}`
            : 'Default (no preference)'}
        </Text>
      </Box>

      <Divider />

      {/* Catalog list */}
      <ScrollArea h={300}>
        <Stack gap="xs">
          {readyEntries.map((entry) => (
            <Card
              key={entry.overlay.id}
              withBorder
              padding="sm"
              role="button"
              aria-label={`${entry.overlay.name} v${entry.overlay.version}`}
              aria-selected={candidateId === entry.overlay.id}
              onClick={() => handleSelectCandidate(entry)}
              style={{
                cursor: 'pointer',
                borderColor:
                  candidateId === entry.overlay.id
                    ? 'var(--mantine-color-blue-6)'
                    : undefined,
              }}
            >
              <Group justify="space-between">
                <Box>
                  <Text fw={500}>{entry.displayName}</Text>
                  <Text size="sm" c="dimmed">{entry.description}</Text>
                  <Group gap="xs" mt={4}>
                    {entry.tags.slice(0, 4).map((t) => (
                      <Badge key={t} size="xs" variant="light">{t}</Badge>
                    ))}
                  </Group>
                </Box>
                {candidateId === entry.overlay.id && (
                  <IconCheck size={18} aria-label="selected" />
                )}
              </Group>
            </Card>
          ))}
        </Stack>
      </ScrollArea>

      {/* Preview area
          2026-09-18 (PROMPT F 21) — iframe path now actually renders.
          - iframe: only when previewKind='iframe' AND overlay.previewIframe is set.
            Sandbox attribute restricts what the iframe can do (no top-nav,
            no parent access). Same-origin only — CSP gates this; cross-origin
            needs a CSP amendment (frame-src host).
          - thumbnail: static preview. Shows previewThumbnail if set, otherwise
            the gradient fallback. */}
      {candidateId && (
        <Box>
          <Text size="sm" c="dimmed" mb={4}>Preview</Text>
          <Box
            style={{
              border: '1px solid var(--mantine-color-default-border)',
              borderRadius: 8,
              overflow: 'hidden',
              background:
                CATALOG.find((e) => e.overlay.id === candidateId)?.overlay
                  .previewThumbnail
                  ? undefined
                  : 'linear-gradient(135deg, #f0f4ff 0%, #e0e7ff 50%, #c7d2fe 100%)',
              minHeight: 120,
            }}
          >
            {(() => {
              const candidate = CATALOG.find((e) => e.overlay.id === candidateId);
              const hasIframe =
                previewKind === 'iframe' && candidate?.overlay.previewIframe;
              if (hasIframe) {
                return (
                  <iframe
                    title={`${candidate!.overlay.name} preview`}
                    src={candidate!.overlay.previewIframe}
                    sandbox="allow-scripts allow-same-origin"
                    style={{
                      width: '100%',
                      height: 180,
                      border: 0,
                      display: 'block',
                      background: '#fff',
                    }}
                  />
                );
              }
              if (candidate?.overlay.previewThumbnail) {
                return (
                  <img
                    src={candidate.overlay.previewThumbnail}
                    alt={`${candidate.overlay.name} thumbnail`}
                    style={{
                      width: '100%',
                      height: 180,
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                );
              }
              return (
                <Box p="md">
                  <Text size="sm" c="dimmed">
                    Static preview (gradient fallback). Add{' '}
                    <code>previewThumbnail</code> or <code>previewIframe</code> to
                    the overlay manifest to enable rich preview.
                  </Text>
                </Box>
              );
            })()}
          </Box>
        </Box>
      )}

      {/* Scope picker */}
      <Box>
        <Text size="sm" fw={500} mb={4}>Apply scope</Text>
        <Radio.Group
          value={scope.surfaceId ? 'surface' : scope.projectId ? 'project' : 'account'}
          onChange={(v) => {
            if (v === 'account') setScope({ userId: scope.userId });
            if (v === 'project') setScope({ userId: scope.userId, projectId: 'current' });
            if (v === 'surface') setScope({ userId: scope.userId, projectId: 'current', surfaceId: 'current' });
          }}
        >
          <Group>
            <Radio value="account" label="Account" />
            <Radio value="project" label="Project" />
            <Radio value="surface" label="Surface" />
          </Group>
        </Radio.Group>
        <Text size="xs" c="dimmed" mt={4}>
          Most-specific wins: surface overrides project overrides account.
        </Text>
      </Box>

      <Divider />

      {/* Actions */}
      <Group justify="space-between">
        <Group>
          <Button
            leftSection={<IconEye size={16} />}
            onClick={handleRequestApply}
            disabled={!candidateId || applying}
            loading={applying}
            variant="filled"
          >
            Apply
          </Button>
          <Button
            leftSection={<IconTrash size={16} />}
            onClick={handleRevert}
            disabled={applying}
            color="orange"
            variant="light"
          >
            Revert
          </Button>
        </Group>
        <Button
          leftSection={<IconX size={16} />}
          onClick={handleCancel}
          variant="subtle"
        >
          Cancel
        </Button>
      </Group>

      {/* Diff modal (shown BEFORE applying — operator rule) */}
      <Modal
        opened={showDiff}
        onClose={handleDiffCancel}
        title="Confirm overlay change"
        size="lg"
      >
        {diffData && (
          <Stack gap="md">
            <Text size="sm">
              You are switching from{' '}
              <Text component="span" fw={500}>
                {diffData.current ? diffData.current.name : '(default)'}
              </Text>{' '}
              to{' '}
              <Text component="span" fw={500}>
                {diffData.candidate.name}
              </Text>
              .
            </Text>
            <Box>
              <Text size="sm" fw={500} mb={4}>Changed fields:</Text>
              {diffData.changedFields.length === 0 ? (
                <Text size="sm" c="dimmed">No changes</Text>
              ) : (
                <Stack gap={2}>
                  {diffData.changedFields.map((f) => (
                    <Text key={f} size="sm" c="dimmed">• {f}</Text>
                  ))}
                </Stack>
              )}
            </Box>
            <Group justify="flex-end">
              <Button variant="subtle" onClick={handleDiffCancel}>
                Cancel
              </Button>
              <Button onClick={handleConfirmApply} loading={applying}>
                Confirm apply
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Convenience: modal wrapper for callers (Settings tab, palette, etc.)
//
// F 99.1 — REFACTORED to use the generic <Modal> primitive from F 96.
// (Previously used Mantine's Modal directly with title="Pick an overlay".)
// ---------------------------------------------------------------------------

import { Modal as FCModal } from '../../primitives/Modal';

export interface OverlayPickerModalProps extends OverlayPickerProps {
  opened: boolean;
  onClose: () => void;
}

export function OverlayPickerModal({ opened, onClose, ...rest }: OverlayPickerModalProps) {
  return (
    <FCModal
      opened={opened}
      onClose={onClose}
      maxWidth={960}
      ariaLabel="Pick an overlay"
      header="Pick an overlay"
    >
      <OverlayPicker {...rest} onClose={onClose} />
    </FCModal>
  );
}

export default OverlayPicker;
